import type { PaigeClient } from '@paige/api-client';
import type {
  EmailPreferences,
  EmailPreferencesChange,
  Inbox,
  NotificationMute,
  NotificationPreferences,
} from './types';
import { tenantHeader, unwrap } from './http';

/** Which mail an unsubscribe link stops. */
export type UnsubscribeCategory = 'tips' | 'getting_started' | 'learning_notes';

/** A learning note's link: its company (`n`) and job (`j`). */
export type LinkNote = { n: string; j: string };

/** The in-app inbox and its preferences (IMPLEMENTATION.md row 41), per person per company. */
export interface NotificationsApi {
  inbox(tenant: string): Promise<Inbox>;
  /** Marks the given notifications read, or every one when no ids are given. */
  markRead(tenant: string, ids?: string[]): Promise<Inbox>;
  preferences(tenant: string): Promise<NotificationPreferences>;
  setPreferences(
    tenant: string,
    preferences: NotificationPreferences,
  ): Promise<NotificationPreferences>;
  mute(tenant: string, mute: NotificationMute): Promise<NotificationPreferences>;
  /** The signed-in person's lifecycle mail choices: tips, getting started, account (always on). */
  emailPreferences(): Promise<EmailPreferences>;
  /** Turning tips on opts in for this company, to the person's own address. */
  setEmailPreferences(tenant: string, change: EmailPreferencesChange): Promise<EmailPreferences>;
  /** The same choices through an email's link (`t`), with no session. */
  linkPreferences(token: string): Promise<EmailPreferences>;
  setLinkPreferences(token: string, change: EmailPreferencesChange): Promise<EmailPreferences>;
  /**
   * The unsubscribe page's button: one category off, or both without one; a learning note's link stops that one note
   * (its company and job, `note`).
   */
  unsubscribe(token: string, category?: UnsubscribeCategory, note?: LinkNote): Promise<void>;
}

const h = (tenant: string) => ({ params: { header: tenantHeader(tenant) } });
/** The writes carry the contract's idempotency header; the client fills the key. */
const w = (tenant: string) => ({
  params: { header: { ...tenantHeader(tenant), 'Idempotency-Key': '' } },
});

export function notificationsApi(client: PaigeClient): NotificationsApi {
  return {
    async inbox(tenant) {
      return unwrap(await client.GET('/notifications', h(tenant))) as Inbox;
    },
    async markRead(tenant, ids) {
      return unwrap(
        await client.POST('/notifications/read', {
          ...w(tenant),
          body: ids ? { ids } : { all: true },
        }),
      ) as Inbox;
    },
    async preferences(tenant) {
      return unwrap(
        await client.GET('/notifications/preferences', h(tenant)),
      ) as NotificationPreferences;
    },
    async setPreferences(tenant, preferences) {
      return unwrap(
        await client.PUT('/notifications/preferences', { ...w(tenant), body: preferences }),
      ) as NotificationPreferences;
    },
    async mute(tenant, mute) {
      return unwrap(
        await client.POST('/notifications/mute', { ...w(tenant), body: mute }),
      ) as NotificationPreferences;
    },
    ...emailPreferencesApi(client),
  };
}

type EmailPreferencesApi = Pick<
  NotificationsApi,
  | 'emailPreferences'
  | 'setEmailPreferences'
  | 'linkPreferences'
  | 'setLinkPreferences'
  | 'unsubscribe'
>;

function emailPreferencesApi(client: PaigeClient): EmailPreferencesApi {
  const link = (t: string) => ({ params: { query: { t } } });
  return {
    async emailPreferences() {
      return unwrap(await client.GET('/me/email-preferences')) as EmailPreferences;
    },
    async setEmailPreferences(tenant, change) {
      return unwrap(
        await client.PUT('/me/email-preferences', { ...h(tenant), body: change }),
      ) as EmailPreferences;
    },
    async linkPreferences(token) {
      return unwrap(await client.GET('/mail/preferences', link(token))) as EmailPreferences;
    },
    async setLinkPreferences(token, change) {
      return unwrap(
        await client.PUT('/mail/preferences', { ...link(token), body: change }),
      ) as EmailPreferences;
    },
    async unsubscribe(token, category, note) {
      const query = category ? { t: token, c: category, ...(note ?? {}) } : { t: token };
      unwrap(await client.POST('/mail/unsubscribe', { params: { query } }));
    },
  };
}
